<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ApiClient from 'dashboard/api/ApiClient';
import LabelsAPI from 'dashboard/api/labels';
import InboxesAPI from 'dashboard/api/inboxes';
import APIHelper from 'dashboard/helper/APIHelper';
import { useAlert } from 'dashboard/composables';
// Removed SettingsLayout imports - using standard dashboard layout
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { Multiselect } from 'vue-multiselect';

const { t } = useI18n();

// Data
const isLoading = ref(false);
const bulkMessages = ref([]);
const showCreateForm = ref(false);
const previewCount = ref(null);
const form = ref({
  name: '',
  message: '',
  image_url: '',
  scheduled_at: '',
  selectedLabels: [],
  selectedInboxes: [],
  selectedChannels: [],
});
const availableLabels = ref([]);
const availableInboxes = ref([]);
const availableChannels = ref([]);
const pollingInterval = ref(null);

// Dialog refs
const deleteDialogRef = ref(null);
const scheduleDialogRef = ref(null);
const selectedBulkMessageId = ref(null);
const scheduledTime = ref('');

// API
const wootAPI = new ApiClient('bulk_messages', { accountScoped: true });
const authenticatedAxios = APIHelper(window.axios);

// Computed
const labelOptions = computed(() => availableLabels.value || []);
const inboxOptions = computed(() => availableInboxes.value || []);
const channelOptions = computed(() => availableChannels.value || []);

const statusMap = computed(() => ({
  pending: t('BULK_MESSAGES.STATUS.PENDING'),
  processing: t('BULK_MESSAGES.STATUS.PROCESSING'),
  completed: t('BULK_MESSAGES.STATUS.COMPLETED'),
  failed: t('BULK_MESSAGES.STATUS.FAILED'),
  cancelled: t('BULK_MESSAGES.STATUS.CANCELLED'),
  scheduled: t('BULK_MESSAGES.STATUS.SCHEDULED'),
  paused: t('BULK_MESSAGES.STATUS.PAUSED'),
  resumed: t('BULK_MESSAGES.STATUS.RESUMED'),
}));

// Methods
const loadBulkMessages = async () => {
  try {
    isLoading.value = true;
    const response = await wootAPI.get();
    if (Array.isArray(response.data)) {
      bulkMessages.value = response.data;
    } else if (Array.isArray(response.data.payload)) {
      bulkMessages.value = response.data.payload;
    } else {
      bulkMessages.value = [];
    }
  } catch (error) {
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const loadChannels = () => {
  availableChannels.value = availableInboxes.value
    .filter(inbox => inbox.channel_type === 'Channel::Telegram')
    .map(inbox => ({
      label: `${inbox.name} (${inbox.channel?.bot_name || 'Telegram'})`,
      value: inbox.id,
      inbox_id: inbox.id,
      channel_type: 'telegram',
    }));
};

const closeCreateForm = () => {
  showCreateForm.value = false;
  form.value = {
    name: '',
    message: '',
    image_url: '',
    scheduled_at: '',
    selectedLabels: [],
    selectedInboxes: [],
    selectedChannels: [],
  };
  previewCount.value = null;
};

const loadFilters = async () => {
  try {
    // Load labels
    const labelsResponse = await LabelsAPI.get();
    if (Array.isArray(labelsResponse.data.payload)) {
      availableLabels.value = labelsResponse.data.payload;
    } else if (Array.isArray(labelsResponse.data)) {
      availableLabels.value = labelsResponse.data;
    } else {
      availableLabels.value = [];
    }

    // Load inboxes
    const inboxesResponse = await InboxesAPI.get();
    if (Array.isArray(inboxesResponse.data.payload)) {
      availableInboxes.value = inboxesResponse.data.payload;
    } else if (Array.isArray(inboxesResponse.data)) {
      availableInboxes.value = inboxesResponse.data;
    } else {
      availableInboxes.value = [];
    }

    // Load channels (Telegram inboxes)
    loadChannels();
  } catch (error) {
    availableLabels.value = [];
    availableInboxes.value = [];
    availableChannels.value = [];
  }
};

const previewBulkMessage = async () => {
  try {
    const response = await authenticatedAxios.post(
      '/api/v1/accounts/1/bulk_messages/preview',
      {
        label_filter: JSON.stringify(form.value.selectedLabels.map(l => l.id)),
        inbox_filter: JSON.stringify(form.value.selectedInboxes.map(i => i.id)),
        channel_filter: JSON.stringify(
          form.value.selectedChannels.map(c => c.inbox_id)
        ),
      }
    );
    previewCount.value = response.data.target_count;
  } catch (error) {
    // Error previewing bulk message
  }
};

const createBulkMessage = async () => {
  try {
    isLoading.value = true;
    await wootAPI.create({
      bulk_message: {
        name: form.value.name,
        message: form.value.message,
        image_url: form.value.image_url,
        scheduled_at: form.value.scheduled_at || null,
        label_filter: JSON.stringify(form.value.selectedLabels.map(l => l.id)),
        inbox_filter: JSON.stringify(form.value.selectedInboxes.map(i => i.id)),
        channel_filter: JSON.stringify(
          form.value.selectedChannels.map(c => c.inbox_id)
        ),
      },
    });

    closeCreateForm();
    await loadBulkMessages();

    const message = form.value.scheduled_at
      ? t('BULK_MESSAGES.TOAST_MESSAGES.CREATE_SCHEDULED')
      : t('BULK_MESSAGES.TOAST_MESSAGES.CREATE_SUCCESS');

    useAlert(message);
  } catch (error) {
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.CREATE_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const cancelBulkMessage = async id => {
  try {
    await authenticatedAxios.post(
      `/api/v1/accounts/1/bulk_messages/${id}/cancel`
    );
    await loadBulkMessages();
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.CANCEL_SUCCESS'));
  } catch (error) {
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.CANCEL_ERROR'));
  }
};

const openDeleteDialog = id => {
  selectedBulkMessageId.value = id;
  deleteDialogRef.value?.open();
};

const deleteBulkMessage = async () => {
  if (!selectedBulkMessageId.value) return;

  try {
    await authenticatedAxios.delete(`/api/v1/accounts/1/bulk_messages/${selectedBulkMessageId.value}`);
    await loadBulkMessages();
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.DELETE_SUCCESS'));
    deleteDialogRef.value?.close();
  } catch (error) {
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.DELETE_ERROR'));
  }
};

const pauseBulkMessage = async id => {
  try {
    await authenticatedAxios.post(
      `/api/v1/accounts/1/bulk_messages/${id}/pause`
    );
    await loadBulkMessages();
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.PAUSE_SUCCESS'));
  } catch (error) {
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.PAUSE_ERROR'));
  }
};

const resumeBulkMessage = async id => {
  try {
    await authenticatedAxios.post(
      `/api/v1/accounts/1/bulk_messages/${id}/resume`
    );
    await loadBulkMessages();
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.RESUME_SUCCESS'));
  } catch (error) {
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.RESUME_ERROR'));
  }
};

const openScheduleDialog = id => {
  selectedBulkMessageId.value = id;
  scheduledTime.value = '';
  scheduleDialogRef.value?.open();
};

const scheduleBulkMessage = async () => {
  if (!selectedBulkMessageId.value || !scheduledTime.value) return;

  try {
    const scheduledDateTime = new Date(scheduledTime.value).toISOString();
    await authenticatedAxios.post(
      `/api/v1/accounts/1/bulk_messages/${selectedBulkMessageId.value}/schedule`,
      {
        scheduled_at: scheduledDateTime,
      }
    );
    await loadBulkMessages();
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.SCHEDULE_SUCCESS'));
    scheduleDialogRef.value?.close();
  } catch (error) {
    useAlert(t('BULK_MESSAGES.TOAST_MESSAGES.SCHEDULE_ERROR'));
  }
};

const canCancelBulkMessage = status => {
  return ['pending', 'scheduled', 'paused', 'resumed'].includes(status);
};

const updateProgressOnly = async () => {
  const activeMessages = bulkMessages.value.filter(bm =>
    ['processing', 'pending', 'resumed'].includes(bm.status)
  );
  if (activeMessages.length === 0) return;

  try {
    const response = await wootAPI.get();
    let updatedMessages = [];
    if (Array.isArray(response.data)) {
      updatedMessages = response.data;
    } else if (Array.isArray(response.data.payload)) {
      updatedMessages = response.data.payload;
    }

    bulkMessages.value = bulkMessages.value.map(bm => {
      if (['processing', 'pending', 'resumed'].includes(bm.status)) {
        const updated = updatedMessages.find(um => um.id === bm.id);
        return updated || bm;
      }
      return bm;
    });
  } catch (error) {
    // Error updating progress
  }
};

const startPolling = () => {
  pollingInterval.value = setInterval(() => {
    updateProgressOnly();
  }, 2000);
};

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value);
    pollingInterval.value = null;
  }
};

const formatDate = dateString => {
  return new Date(dateString).toLocaleDateString();
};

const getStatusColor = status => {
  const colors = {
    pending: 'text-n-amber-11 bg-n-amber-2',
    processing: 'text-n-blue-11 bg-n-blue-2',
    completed: 'text-n-emerald-11 bg-n-emerald-2',
    failed: 'text-n-ruby-11 bg-n-ruby-2',
    cancelled: 'text-n-slate-11 bg-n-slate-2',
    scheduled: 'text-n-purple-11 bg-n-purple-2',
    paused: 'text-n-orange-11 bg-n-orange-2',
    resumed: 'text-n-teal-11 bg-n-teal-2',
  };
  return colors[status] || 'text-n-slate-11 bg-n-slate-2';
};

// Lifecycle
onMounted(async () => {
  await loadBulkMessages();
  await loadFilters();
  startPolling();
});

onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Header Section -->
    <div class="sticky top-0 z-20 bg-n-background border-b border-n-container">
      <div class="px-6 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 bg-n-blue-2 rounded-lg flex items-center justify-center"
            >
              <fluent-icon icon="mail" class="text-n-blue-11" size="20" />
            </div>
            <div>
              <h1 class="text-xl font-semibold text-n-slate-12">
                {{ t('BULK_MESSAGES.TITLE') }}
              </h1>
              <p class="text-sm text-n-slate-11">
                {{ t('BULK_MESSAGES.DESCRIPTION.SEND_TO_ALL') }}
              </p>
            </div>
          </div>
          <Button
            icon="i-lucide-circle-plus"
            :label="t('BULK_MESSAGES.NEW_BULK_MESSAGE')"
            @click="showCreateForm = true"
          />
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 overflow-auto p-6">
      <!-- Loading State -->
      <div v-if="isLoading" class="flex items-center justify-center py-20">
        <woot-loading-state :message="t('BULK_MESSAGES.LOADING')" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!bulkMessages.length"
        class="flex flex-col items-center justify-center py-20"
      >
        <div
          class="w-16 h-16 bg-n-slate-2 rounded-full flex items-center justify-center mb-4"
        >
          <fluent-icon icon="mail" class="text-n-slate-11" size="32" />
        </div>
        <h3 class="text-lg font-medium text-n-slate-12 mb-2">
          {{ t('BULK_MESSAGES.LIST.EMPTY_TITLE') }}
        </h3>
        <p class="text-n-slate-11 mb-6 text-center max-w-md">
          {{ t('BULK_MESSAGES.LIST.EMPTY_SUBTITLE') }}
        </p>
        <Button
          icon="i-lucide-circle-plus"
          :label="t('BULK_MESSAGES.LIST.EMPTY_BUTTON')"
          @click="showCreateForm = true"
        />
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Stats Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div
            class="flex items-center p-4 bg-n-solid-2 rounded-xl border border-n-container"
          >
            <div
              class="w-10 h-10 bg-n-blue-2 rounded-lg flex items-center justify-center mr-4 flex-shrink-0"
            >
              <fluent-icon icon="mail" class="text-n-blue-11" size="20" />
            </div>
            <div class="min-w-0">
              <p class="text-sm text-n-slate-11">
                {{ t('BULK_MESSAGES.STATS.TOTAL') }}
              </p>
              <p class="text-2xl font-semibold text-n-slate-12">
                {{ bulkMessages.length }}
              </p>
            </div>
          </div>

          <div
            class="flex items-center p-4 bg-n-solid-2 rounded-xl border border-n-container"
          >
            <div
              class="w-10 h-10 bg-n-emerald-2 rounded-lg flex items-center justify-center mr-4 flex-shrink-0"
            >
              <fluent-icon
                icon="checkmark-circle"
                class="text-n-emerald-11"
                size="20"
              />
            </div>
            <div class="min-w-0">
              <p class="text-sm text-n-slate-11">
                {{ t('BULK_MESSAGES.STATS.COMPLETED') }}
              </p>
              <p class="text-2xl font-semibold text-n-slate-12">
                {{
                  bulkMessages.filter(bm => bm.status === 'completed').length
                }}
              </p>
            </div>
          </div>

          <div
            class="flex items-center p-4 bg-n-solid-2 rounded-xl border border-n-container"
          >
            <div
              class="w-10 h-10 bg-n-blue-2 rounded-lg flex items-center justify-center mr-4 flex-shrink-0"
            >
              <fluent-icon icon="clock" class="text-n-blue-11" size="20" />
            </div>
            <div class="min-w-0">
              <p class="text-sm text-n-slate-11">
                {{ t('BULK_MESSAGES.STATS.PROCESSING') }}
              </p>
              <p class="text-2xl font-semibold text-n-slate-12">
                {{
                  bulkMessages.filter(bm => bm.status === 'processing').length
                }}
              </p>
            </div>
          </div>

          <div
            class="flex items-center p-4 bg-n-solid-2 rounded-xl border border-n-container"
          >
            <div
              class="w-10 h-10 bg-n-purple-2 rounded-lg flex items-center justify-center mr-4 flex-shrink-0"
            >
              <fluent-icon icon="calendar" class="text-n-purple-11" size="20" />
            </div>
            <div class="min-w-0">
              <p class="text-sm text-n-slate-11">
                {{ t('BULK_MESSAGES.STATS.SCHEDULED') }}
              </p>
              <p class="text-2xl font-semibold text-n-slate-12">
                {{
                  bulkMessages.filter(bm => bm.status === 'scheduled').length
                }}
              </p>
            </div>
          </div>
        </div>
        <!-- FIXED: closed the stats cards div above -->

        <!-- Bulk Messages Table -->
        <div
          class="bg-n-solid-2 rounded-xl border border-n-container overflow-hidden"
        >
          <div class="px-6 py-4 border-b border-n-container">
            <h3 class="text-lg font-medium text-n-slate-12">
              {{ t('BULK_MESSAGES.LIST.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11 mt-1">
              {{ t('BULK_MESSAGES.LIST.SUBTITLE') }}
            </p>
          </div>

          <div v-if="bulkMessages.length === 0" class="p-12 text-center">
            <div
              class="w-16 h-16 bg-n-slate-2 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <fluent-icon icon="mail" class="text-n-slate-11" size="32" />
            </div>
            <h3 class="text-lg font-medium text-n-slate-12 mb-2">
              {{ t('BULK_MESSAGES.LIST.EMPTY_TITLE') }}
            </h3>
            <p class="text-n-slate-11 mb-6">
              {{ t('BULK_MESSAGES.LIST.EMPTY_SUBTITLE') }}
            </p>
            <Button
              icon="i-lucide-circle-plus"
              :label="t('BULK_MESSAGES.LIST.EMPTY_BUTTON')"
              @click="showCreateForm = true"
            />
          </div>

          <div v-else class="divide-y divide-n-container">
            <div
              v-for="bulkMessage in bulkMessages"
              :key="bulkMessage.id"
              class="p-4 sm:p-6 hover:bg-n-slate-1 transition-colors duration-200"
            >
              <div
                class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4"
              >
                <div class="flex-1 min-w-0">
                  <div
                    class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2"
                  >
                    <h4 class="text-lg font-medium text-n-slate-12 truncate">
                      {{ bulkMessage.name }}
                    </h4>
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium w-fit"
                      :class="getStatusColor(bulkMessage.status)"
                    >
                      {{ statusMap[bulkMessage.status] }}
                    </span>
                  </div>

                  <p class="text-sm text-n-slate-11 mb-4 line-clamp-2">
                    {{ bulkMessage.message }}
                  </p>

                  <!-- Stats -->
                  <div
                    class="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-3 sm:gap-6 text-sm text-n-slate-11 mb-4"
                  >
                    <div class="flex items-center">
                      <fluent-icon
                        icon="people"
                        class="w-4 h-4 mr-1 flex-shrink-0"
                      />
                      <span class="font-medium">{{
                        bulkMessage.total_count
                      }}</span>
                      <span class="hidden sm:inline">{{
                        t('BULK_MESSAGES.LIST.TARGET')
                      }}</span>
                    </div>
                    <div class="flex items-center">
                      <fluent-icon
                        icon="checkmark-circle"
                        class="w-4 h-4 mr-1 text-n-emerald-11 flex-shrink-0"
                      />
                      <span class="font-medium">{{
                        bulkMessage.sent_count
                      }}</span>
                      <span class="hidden sm:inline">{{
                        t('BULK_MESSAGES.LIST.SENT')
                      }}</span>
                    </div>
                    <div
                      v-if="bulkMessage.failed_count > 0"
                      class="flex items-center"
                    >
                      <fluent-icon
                        icon="error-circle"
                        class="w-4 h-4 mr-1 text-n-ruby-11 flex-shrink-0"
                      />
                      <span class="font-medium">{{
                        bulkMessage.failed_count
                      }}</span>
                      <span class="hidden sm:inline">{{
                        t('BULK_MESSAGES.LIST.ERROR')
                      }}</span>
                    </div>
                    <div class="flex items-center col-span-2 sm:col-span-1">
                      <fluent-icon
                        icon="clock"
                        class="w-4 h-4 mr-1 flex-shrink-0"
                      />
                      {{ formatDate(bulkMessage.created_at) }}
                    </div>
                  </div>

                  <!-- Progress Bar -->
                  <div v-if="bulkMessage.status === 'processing'" class="mb-4">
                    <div
                      class="flex items-center justify-between text-sm text-n-slate-11 mb-2"
                    >
                      <span>{{ t('BULK_MESSAGES.LIST.PROGRESS') }}</span>
                      <span class="font-medium">
                        {{ bulkMessage.progress_percentage }}%
                      </span>
                    </div>
                    <div class="w-full bg-n-slate-3 rounded-full h-2">
                      <div
                        class="bg-n-blue-9 h-2 rounded-full transition-all duration-500 ease-out"
                        :style="{
                          width: `${bulkMessage.progress_percentage}%`,
                        }"
                      />
                    </div>
                  </div>
                </div>

                <!-- Actions -->
                <div class="flex flex-wrap items-center gap-2 sm:ml-4">
                  <Button
                    v-if="bulkMessage.status === 'processing'"
                    icon="i-lucide-pause"
                    :label="t('BULK_MESSAGES.ACTIONS.PAUSE')"
                    slate
                    xs
                    @click="pauseBulkMessage(bulkMessage.id)"
                  />

                  <Button
                    v-if="bulkMessage.status === 'paused'"
                    icon="i-lucide-play"
                    :label="t('BULK_MESSAGES.ACTIONS.RESUME')"
                    blue
                    xs
                    @click="resumeBulkMessage(bulkMessage.id)"
                  />

                  <Button
                    v-if="bulkMessage.status === 'scheduled'"
                    icon="i-lucide-calendar"
                    :label="t('BULK_MESSAGES.ACTIONS.SCHEDULE')"
                    purple
                    xs
                    @click="openScheduleDialog(bulkMessage.id)"
                  />

                  <Button
                    v-if="canCancelBulkMessage(bulkMessage.status)"
                    icon="i-lucide-x"
                    :label="t('BULK_MESSAGES.ACTIONS.CANCEL')"
                    slate
                    xs
                    @click="cancelBulkMessage(bulkMessage.id)"
                  />

                  <Button
                    icon="i-lucide-trash-2"
                    :label="t('BULK_MESSAGES.ACTIONS.DELETE')"
                    ruby
                    xs
                    @click="openDeleteDialog(bulkMessage.id)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Create Form Modal -->
  <woot-modal
    v-model:show="showCreateForm"
    size="large"
    :on-close="closeCreateForm"
  >
    <div class="p-6">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-xl font-semibold text-n-slate-12">
            {{ t('BULK_MESSAGES.MODAL.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11 mt-1">
            {{ t('BULK_MESSAGES.MODAL.SUBTITLE') }}
          </p>
        </div>
        <button
          class="text-n-slate-11 hover:text-n-slate-12 transition-colors duration-200"
          @click="closeCreateForm"
        >
          <fluent-icon icon="dismiss" size="20" />
        </button>
      </div>

      <form @submit.prevent="createBulkMessage">
        <!-- Basic Info -->
        <div class="grid grid-cols-1 gap-6 mb-6">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ t('BULK_MESSAGES.FORM.MESSAGE_NAME') }} *
            </label>
            <input
              v-model="form.name"
              type="text"
              required
              class="w-full px-4 py-3 border border-n-container rounded-lg focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent bg-n-solid-2 text-n-slate-12 transition-colors duration-200"
              :placeholder="t('BULK_MESSAGES.FORM.MESSAGE_NAME_PLACEHOLDER')"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ t('BULK_MESSAGES.FORM.MESSAGE_CONTENT') }} *
            </label>
            <textarea
              v-model="form.message"
              required
              rows="4"
              class="w-full px-4 py-3 border border-n-container rounded-lg focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent bg-n-solid-2 text-n-slate-12 transition-colors duration-200 resize-none"
              :placeholder="t('BULK_MESSAGES.FORM.MESSAGE_CONTENT_PLACEHOLDER')"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ t('BULK_MESSAGES.FORM.IMAGE_URL') }}
            </label>
            <input
              v-model="form.image_url"
              type="url"
              class="w-full px-4 py-3 border border-n-container rounded-lg focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent bg-n-solid-2 text-n-slate-12 transition-colors duration-200"
              :placeholder="t('BULK_MESSAGES.FORM.IMAGE_URL_PLACEHOLDER')"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              {{ t('BULK_MESSAGES.FORM.SCHEDULED_SENDING') }}
            </label>
            <div
              class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
            >
              <input
                v-model="form.scheduled_at"
                type="datetime-local"
                class="flex-1 px-4 py-3 border border-n-container rounded-lg focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent bg-n-solid-2 text-n-slate-12 transition-colors duration-200"
              />
              <Button
                type="button"
                slate
                :label="t('BULK_MESSAGES.FORM.CLEAR')"
                @click="form.scheduled_at = ''"
              />
            </div>
            <p class="text-xs text-n-slate-11 mt-1">
              {{ t('BULK_MESSAGES.FORM.SCHEDULED_HELP') }}
            </p>
          </div>
        </div>

        <!-- Filters -->
        <div class="border-t border-n-container pt-6">
          <h3 class="text-lg font-medium text-n-slate-12 mb-4">
            {{ t('BULK_MESSAGES.FORM.TARGET_FILTERS') }}
          </h3>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                {{ t('BULK_MESSAGES.FORM.LABEL_FILTER') }}
              </label>
              <Multiselect
                v-model="form.selectedLabels"
                :options="labelOptions"
                multiple
                :placeholder="t('BULK_MESSAGES.FORM.LABEL_FILTER_PLACEHOLDER')"
                label="title"
                track-by="id"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                {{ t('BULK_MESSAGES.FORM.INBOX_FILTER') }}
              </label>
              <Multiselect
                v-model="form.selectedInboxes"
                :options="inboxOptions"
                multiple
                :placeholder="t('BULK_MESSAGES.FORM.INBOX_FILTER_PLACEHOLDER')"
                label="name"
                track-by="id"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                {{ t('BULK_MESSAGES.FORM.CHANNEL_FILTER') }}
              </label>
              <Multiselect
                v-model="form.selectedChannels"
                :options="channelOptions"
                multiple
                :placeholder="
                  t('BULK_MESSAGES.FORM.CHANNEL_FILTER_PLACEHOLDER')
                "
                label="label"
                track-by="value"
              />
            </div>
          </div>
        </div>

        <!-- Preview -->
        <div
          v-if="previewCount !== null"
          class="mt-6 p-4 bg-n-blue-2 rounded-lg"
        >
          <p class="text-sm text-n-blue-11">
            {{
              t('BULK_MESSAGES.PREVIEW.TARGET_COUNT', { count: previewCount })
            }}
          </p>
        </div>

        <!-- Actions -->
        <div
          class="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 mt-8 pt-6 border-t border-n-container"
        >
          <Button
            type="button"
            slate
            :label="t('BULK_MESSAGES.MODAL.CANCEL')"
            @click="closeCreateForm"
          />
          <Button
            type="button"
            slate
            :label="t('BULK_MESSAGES.MODAL.PREVIEW')"
            @click="previewBulkMessage"
          />
          <Button
            type="submit"
            blue
            :label="t('BULK_MESSAGES.MODAL.CREATE')"
            :is-loading="isLoading"
            :disabled="!form.name || !form.message"
          />
        </div>
      </form>
    </div>
  </woot-modal>

  <!-- Delete Confirmation Dialog -->
  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="t('BULK_MESSAGES.CONFIRM.DELETE_TITLE')"
    :description="t('BULK_MESSAGES.CONFIRM.DELETE_DESCRIPTION')"
    :confirm-button-label="t('BULK_MESSAGES.CONFIRM.DELETE_CONFIRM')"
    :cancel-button-label="t('BULK_MESSAGES.CONFIRM.DELETE_CANCEL')"
    @confirm="deleteBulkMessage"
  />

  <!-- Schedule Dialog -->
  <Dialog
    ref="scheduleDialogRef"
    type="edit"
    :title="t('BULK_MESSAGES.CONFIRM.SCHEDULE_TITLE')"
    :description="t('BULK_MESSAGES.CONFIRM.SCHEDULE_DESCRIPTION')"
    :confirm-button-label="t('BULK_MESSAGES.CONFIRM.SCHEDULE_CONFIRM')"
    :cancel-button-label="t('BULK_MESSAGES.CONFIRM.SCHEDULE_CANCEL')"
    @confirm="scheduleBulkMessage"
  >
    <div class="mt-4">
      <label class="block text-sm font-medium text-n-slate-12 mb-2">
        {{ t('BULK_MESSAGES.CONFIRM.SCHEDULE_DATETIME_LABEL') }}
      </label>
      <input
        v-model="scheduledTime"
        type="datetime-local"
        required
        class="w-full px-4 py-3 border border-n-container rounded-lg focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent bg-n-solid-2 text-n-slate-12 transition-colors duration-200"
        :placeholder="t('BULK_MESSAGES.CONFIRM.SCHEDULE_DATETIME_PLACEHOLDER')"
      />
      <p class="text-xs text-n-slate-11 mt-1">
        {{ t('BULK_MESSAGES.CONFIRM.SCHEDULE_HELP') }}
      </p>
    </div>
  </Dialog>
</template>
